import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('custom-select', false);

/**
 * A dropdown built round a group of radio inputs, so whatever reads the
 * radios keeps working and the styling is the theme's rather than the
 * browser's.
 *
 * Markup it expects inside itself:
 *   [data-select-trigger]  a button that opens and closes the panel and
 *                          shows the chosen option
 *   [data-select-value]    the element inside the trigger that carries the
 *                          chosen option's text and classes
 *   [data-select-panel]    the container holding the radios and their labels
 *   input[type=radio] + label[for]  one pair per option; the label may carry
 *                          data-option-class, which is copied onto the value
 *                          element so the trigger shows the option in its
 *                          own face
 *
 * Anything that checks a radio without firing `change` can dispatch
 * `custom-select:sync` (bubbling) from the radio to bring the trigger up to
 * date.
 */
class CustomSelect extends HTMLElement {
    constructor() {
        super();
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.sync = this.sync.bind(this);
    }

    connectedCallback() {
        this.trigger = this.querySelector('[data-select-trigger]');
        this.valueEl = this.querySelector('[data-select-value]');
        this.panel = this.querySelector('[data-select-panel]');
        this.chevron = this.querySelector('[data-select-chevron]');

        if (!this.trigger || !this.panel || !this.valueEl) {
            debug.error('custom-select: missing trigger, panel or value element');
            return;
        }

        this.valueBaseClass = this.valueEl.className;

        this.trigger.addEventListener('click', this.handleTriggerClick);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('custom-select:sync', this.sync);
        this.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('click', this.handleDocumentClick);

        this.sync();
    }

    disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.handleTriggerClick);
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('custom-select:sync', this.sync);
        this.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('click', this.handleDocumentClick);
    }

    get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    open() {
        this.panel.classList.remove('hidden');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.chevron?.classList.add('rotate-180');
        // Land on the chosen option so the arrow keys move from there.
        const checked = this.querySelector('input[type="radio"]:checked');
        (checked || this.querySelector('input[type="radio"]'))?.focus();
    }

    close({ refocus = false } = {}) {
        if (!this.isOpen) return;
        this.panel.classList.add('hidden');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.chevron?.classList.remove('rotate-180');
        if (refocus) this.trigger.focus();
    }

    /** Copy the chosen option's text and face onto the trigger. */
    sync() {
        const checked = this.querySelector('input[type="radio"]:checked');
        const label = checked ? this.querySelector(`label[for="${CSS.escape(checked.id)}"]`) : null;
        if (!label) return;
        this.valueEl.textContent = label.textContent.trim();
        this.valueEl.className = [this.valueBaseClass, label.dataset.optionClass || ''].join(' ').trim();
    }

    handleTriggerClick(e) {
        e.preventDefault();
        if (this.isOpen) this.close();
        else this.open();
    }

    handleChange(e) {
        if (!e.target.matches('input[type="radio"]')) return;
        this.sync();
        this.close({ refocus: true });
    }

    handleDocumentClick(e) {
        if (!this.isOpen || this.contains(e.target)) return;
        this.close();
    }

    handleKeydown(e) {
        if (e.key === 'Escape' && this.isOpen) {
            e.preventDefault();
            this.close({ refocus: true });
        }
    }
}

if (!customElements.get('custom-select')) customElements.define('custom-select', CustomSelect);
